// src/App.jsx
import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import AppLayout from './components/AppLayout';
import LoginPage from './pages/LoginPage';
import DashboardPage from './pages/DashboardPage';
import OrdersPage from './pages/OrdersPage';
import OrderDetailPage from './pages/OrderDetailPage';
import RidersPage from './pages/RidersPage';
import HistoryPage from './pages/HistoryPage';
import RevenuePage from './pages/RevenuePage';
import AdminPage from './pages/AdminPage';
import LiveMapPage from './pages/LiveMapPage';
import { ToastContainer } from './components/UI';

// At the top of App.jsx, outside any component
fetch('https://logistics-backend-oo5v.onrender.com/health').catch(() => { });
// ─── Error Boundary ─────────────────────────────────────────
class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  resetError = () => {
    this.setState({ hasError: false, error: null });
  };

  render() {
    if (this.state.hasError) {
      return (
        <div style={{ padding: '2rem' }}>
          <h2 style={{ color: 'red' }}>Something went wrong:</h2>
          <pre style={{ fontSize: 12, color: '#333' }}>
            {this.state.error?.message}
          </pre>
          <button onClick={this.resetError}>Try again</button>
        </div>
      );
    }

    return this.props.children;
  }
}


// ─── Route Guards ───────────────────────────────────────────
function ProtectedRoute({ children, adminOnly }) {
  const { isAuthenticated, isLoading, user } = useAuth();

  if (isLoading) {
    return (
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '100vh',
          background: 'var(--primary)'
        }}
      >
        <div style={{ textAlign: 'center', color: 'var(--white)' }}>
          <div style={{ fontSize: 48, marginBottom: 16 }}>🚛</div>
          <div
            style={{
              fontFamily: 'var(--font-display)',
              fontSize: 24,
              fontWeight: 800
            }}
          >
            Honcho Xpress
          </div>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) return <Navigate to="/login" replace />;

  if (adminOnly && user?.role !== 'Admin') {
    return <Navigate to="/" replace />;
  }

  return children;
}


function PublicRoute({ children }) {
  const { isAuthenticated, isLoading } = useAuth();

  if (isLoading) return null;

  return isAuthenticated ? <Navigate to="/" replace /> : children;
}


// ─── Routes ─────────────────────────────────────────────────
function AppRoutes() {
  return (
    <Routes>

      {/* Public Route */}
      <Route
        path="/login"
        element={
          <PublicRoute>
            <LoginPage />
          </PublicRoute>
        }
      />

      {/* Protected Layout */}
      <Route
        path="/"
        element={
          <ProtectedRoute>
            <AppLayout />
          </ProtectedRoute>
        }
      >

        <Route index element={<DashboardPage />} />

        <Route path="orders" element={<OrdersPage />} />

        <Route path="orders/:id" element={<OrderDetailPage />} />

        <Route
          path="map"
          element={
            <ErrorBoundary>
              <LiveMapPage />
            </ErrorBoundary>
          }
        />

        <Route path="riders" element={<RidersPage />} />

        <Route path="history" element={<HistoryPage />} />

        <Route path="revenue" element={<RevenuePage />} />

        <Route
          path="admin"
          element={
            <ProtectedRoute adminOnly>
              <AdminPage />
            </ProtectedRoute>
          }
        />

      </Route>

      {/* Catch All */}
      <Route path="*" element={<Navigate to="/" replace />} />

    </Routes>
  );
}


// ─── App Root ───────────────────────────────────────────────
export default function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <AppRoutes />
        <ToastContainer />
      </AuthProvider>
    </BrowserRouter>
  );
}